import { useLazyQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { Box, Divider, Grid2 as Grid, Stack, Typography } from '@mui/material'
import { isEmpty, isNil, round } from 'lodash'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'

import { DaiDivider } from '@/components/DaiDivider'
import { CommonFilters } from '@/components/filters/filters.helpers'
import ProductDetailsCard from '@/components/ProductDetailsCard'
import StatWithProgress from '@/components/reports/StatWithProgress'
import StickyHeader from '@/components/StickyHeader'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_PRODUCT_CATEGORY_REVENUE_PERCENTAGE,
  GET_PRODUCT_DASHBOARD_INTERVAL_STATS,
  GET_PRODUCT_DASHBOARD_STATS,
  GroupByDate,
  IntervalProductsDashboardStatsQuery,
  IntervalProductsDashboardStatsQueryVariables,
  ProductCategoryRevenuePercentageQuery,
  ProductCategoryRevenuePercentageQueryVariables,
  ProductDashboardIntervalStatFragment,
  ProductsDashboardStatsQuery,
  ProductsDashboardStatsQueryVariables,
} from '@/graphql'
import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import { ChartHelpers } from '@/utils/chart.helpers'
import { DateHelpers } from '@/utils/date.helpers'
import { ProductDashboardStatsHelpers } from '@/utils/productDashboardStats.helpers'
import { computePercentChange, formatAmount } from '@/utils/statCards'

import DAIOverTimeLineChart, {
  DAIOverTimeLineChartSeries,
} from '../charts/ApexCharts/DAIOverTimeLineChart'
import { FilterBar } from '../components/FilterBar'
import { HomeDashboardParams } from '../dashboards/Default/HomeSummary'
import Stats from '../dashboards/Default/Stats'
import StoresProductTable from '../dashboards/Default/StoresProductTable'

const CombinedVolumeVpoDisplay = ({
  volume,
  vpo,
}: {
  volume: number | null
  vpo: number | null
}) => {
  const { t } = useT()

  return (
    <Stack spacing={1} sx={{ width: '100%', textAlign: 'left' }}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('common.total')}
        </Typography>
        <Typography
          variant="h3"
          component="div"
          sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}
        >
          {volume?.toLocaleString() ?? '0'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('common.vpo')}
        </Typography>
        <Typography
          variant="h5"
          component="div"
          sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}
        >
          {vpo?.toLocaleString() ?? '0'}
        </Typography>
      </Box>
    </Stack>
  )
}

export default function ProductDetails() {
  const { state: urlState } = useUrlSearchParams<HomeDashboardParams>()
  const {
    startDate: startDateNoTime,
    endDate: endDateNoTime,
    storeIds,
  } = urlState
  const startDate = startDateNoTime
    ? DateHelpers.getStartOfDayWithTimezone(startDateNoTime)
    : undefined
  const endDate = endDateNoTime
    ? DateHelpers.getEndOfDayWithTimezone(endDateNoTime)
    : undefined
  const { id } = useParams()
  const { t } = useT()

  const addSeries = (
    data: ProductDashboardIntervalStatFragment[],
    series: DAIOverTimeLineChartSeries[],
    statKey: keyof ProductDashboardIntervalStatFragment,
    convertToPercentage: boolean,
    dataType: 'number' | 'currency' | 'percentage',
  ) => {
    const filteredValues = data.map(item => item[statKey])
    series.push({
      id: statKey,
      name: statKey,
      data: filteredValues.map(val =>
        !isNil(val)
          ? round(
              parseFloat((val as number).toFixed(2)) *
                (convertToPercentage ? 100 : 1),
            )
          : 0,
      ),
      dataType,
    })
  }

  const transformProductIntervalDataForCharts = (
    data: ProductDashboardIntervalStatFragment[] | undefined,
  ): DAIOverTimeLineChartSeries[] => {
    if (!data || data.length === 0) {
      return []
    }
    const series: DAIOverTimeLineChartSeries[] = []
    addSeries(data, series, 'totalRevenue', false, 'currency')
    addSeries(data, series, 'totalEarnings', false, 'currency')
    addSeries(data, series, 'volume', false, 'number')
    addSeries(data, series, 'vpo', false, 'number')
    return series
  }

  const [chartDates, setChartDates] = useState<number[]>([])
  const [series, setSeries] = useState<DAIOverTimeLineChartSeries[]>([])

  const [
    fetchProductDashboardIntervalStatsForProduct,
    {
      data: productDashboardIntervalStatsForProduct,
      loading: productDashboardIntervalStatsForProductLoading,
      refetch: refetchProductDashboardIntervalStatsForProduct,
      variables: productDashboardIntervalStatsForProductVariables,
    },
  ] = useLazyQuery<
    IntervalProductsDashboardStatsQuery,
    IntervalProductsDashboardStatsQueryVariables
  >(GET_PRODUCT_DASHBOARD_INTERVAL_STATS, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (productDashboardIntervalStatsForProduct) {
      const filteredResults = DateHelpers.filterFutureDates(
        productDashboardIntervalStatsForProduct.intervalProductsDashboardStats
          .results,
        'date',
      )

      setChartDates(
        filteredResults.map(item =>
          DateHelpers.parseIsoDateWithoutTimezone(item.date),
        ) ?? [],
      )
      setSeries(transformProductIntervalDataForCharts(filteredResults))
    }
  }, [productDashboardIntervalStatsForProduct])

  const revenueSeries = series
    ? series.slice(0, 2).map(item => ({
        id: item.id,
        name: t('lineChart.seriesName', { seriesName: item.name }),
        data: item.data,
        dataType: item.dataType,
      }))
    : []

  const volumeSeries = series
    ? series.slice(2, 4).map(item => ({
        id: item.id,
        name: t('lineChart.seriesName', { seriesName: item.name }),
        data: item.data,
        dataType: item.dataType,
      }))
    : []

  const [
    fetchProductDashboardStatsForProduct,
    {
      data: productDashboardStatsForProduct,
      loading: productDashboardStatsForProductLoading,
      refetch: refetchProductDashboardStatsForProduct,
      variables: productDashboardStatsForProductVariables,
    },
  ] = useLazyQuery<
    ProductsDashboardStatsQuery,
    ProductsDashboardStatsQueryVariables
  >(GET_PRODUCT_DASHBOARD_STATS, { notifyOnNetworkStatusChange: true })

  const [
    fetchProductCategoryRevenuePercentage,
    {
      data: productCategoryRevenuePercentage,
      loading: loadingProductCategoryRevenuePercentage,
      refetch: refetchProductCategoryRevenuePercentage,
      variables: productCategoryRevenuePercentageVariables,
    },
  ] = useLazyQuery<
    ProductCategoryRevenuePercentageQuery,
    ProductCategoryRevenuePercentageQueryVariables
  >(GET_PRODUCT_CATEGORY_REVENUE_PERCENTAGE, {
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    if (startDate && endDate && id) {
      fetchProductDashboardIntervalStatsForProduct({
        variables: {
          filters: {
            storeIds,
            upcs: [id],
            ...ChartHelpers.getChartWindow(
              startDate,
              endDate,
              new Date().toISOString(),
            ),
          },
        },
      })
      fetchProductDashboardStatsForProduct({
        variables: {
          filters: {
            storeIds,
            startDate,
            endDate,
            upcs: [id],
          },
        },
      })
      fetchProductCategoryRevenuePercentage({
        variables: {
          filters: {
            startDate,
            endDate,
            upc: id,
            storeIds,
          },
        },
      })
    }
  }, [startDate, endDate, JSON.stringify(storeIds), id])

  const handleRefresh = () => {
    if (!isEmpty(productDashboardIntervalStatsForProductVariables)) {
      refetchProductDashboardIntervalStatsForProduct()
    }
    if (!isEmpty(productDashboardStatsForProductVariables)) {
      refetchProductDashboardStatsForProduct()
    }
    if (!isEmpty(productCategoryRevenuePercentageVariables)) {
      refetchProductCategoryRevenuePercentage()
    }
  }
  useRefresh(handleRefresh)

  const dataGranularity =
    productDashboardIntervalStatsForProduct?.intervalProductsDashboardStats
      ?.period || GroupByDate.DAY

  return (
    <>
      <Helmet title={t('productDetails.title')} />
      <StickyHeader>
        <FilterBar
          filters={[
            {
              label: 'Other',
              type: 'preCannedDatePicker',
              options: { startDateKey: 'startDate', endDateKey: 'endDate' },
            },
            {
              label: 'Start Date',
              type: 'date',
              options: {
                urlKey: 'startDate',
              },
            },
            {
              label: 'End Date',
              type: 'date',
              options: { urlKey: 'endDate' },
            },
            CommonFilters.StoreFilter(t),
          ]}
        />
        <Divider />
      </StickyHeader>
      <Grid container spacing={3} mt={3}>
        {[
          ProductDashboardStatsHelpers.transformProductCategoryRevenuePercentage(
            productCategoryRevenuePercentage?.categoryRevenuePercentage,
            t,
          ),
          ...ProductDashboardStatsHelpers.transformStats(
            productDashboardStatsForProduct?.productsDashboardStats?.results[0],
            t,
          ),
        ].map(stat => {
          const change =
            isNil(stat.value) ||
            DateHelpers.isSingleDayToday(startDateNoTime, endDateNoTime)
              ? null
              : computePercentChange(stat)
          const isPercentage = stat.valueType === 'PERCENTAGE'
          const isCombinedVolumeVpo = stat.valueType === 'COMBINED_VOLUME_VPO'
          return (
            <Grid
              key={stat.label}
              size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 'grow' }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              {
                <Stats
                  title={stat.label}
                  amount={
                    isNil(stat.value) ? (
                      <Typography variant="h6" gutterBottom>
                        {t('homeDashboard.stats.noData')}
                      </Typography>
                    ) : isCombinedVolumeVpo ? (
                      <CombinedVolumeVpoDisplay
                        volume={stat.value}
                        vpo={(stat as any).perValue}
                      />
                    ) : isPercentage ? (
                      <StatWithProgress value={stat.value || 0} />
                    ) : (
                      formatAmount(stat)
                    )
                  }
                  changeTooltip={
                    startDateNoTime && endDateNoTime
                      ? DateHelpers.yearOverYearTooltipText(
                          startDateNoTime,
                          endDateNoTime,
                          t,
                        )
                      : undefined
                  }
                  change={change}
                  changeTitle={t('statCard.yearOverYear')}
                  loading={
                    productDashboardStatsForProductLoading ||
                    loadingProductCategoryRevenuePercentage
                  }
                />
              }
            </Grid>
          )
        })}
      </Grid>
      <DaiDivider />
      <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
        {id && (
          <ProductDetailsCard
            upc={id}
            fallbackName={
              productDashboardStatsForProduct?.productsDashboardStats
                ?.results[0]?.name || undefined
            }
          />
        )}
      </Grid>
      <DaiDivider />
      <Grid
        container
        spacing={3}
        sx={{ justifyContent: 'center', flexDirection: 'row' }}
      >
        <Grid size={{ xs: 12, lg: 6 }}>
          <DAIOverTimeLineChart
            title={t('common.revenue')}
            series={revenueSeries}
            dates={chartDates}
            loading={productDashboardIntervalStatsForProductLoading}
            dataGranularity={dataGranularity}
            options={{
              stroke: { curve: 'smooth' },
              xaxis: {
                tickAmount: 6,
              },
              yaxis: {
                labels: {
                  formatter: val => (val ? formatCurrency(round(val)) : ''),
                },
              },
              annotations: {
                xaxis: ChartHelpers.getXAxisRangeAnnotation(
                  t,
                  startDate,
                  endDate,
                  dataGranularity,
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <DAIOverTimeLineChart
            title={t('common.volume')}
            series={volumeSeries}
            dates={chartDates}
            loading={productDashboardIntervalStatsForProductLoading}
            dataGranularity={dataGranularity}
            options={{
              stroke: { curve: 'smooth' },
              xaxis: {
                tickAmount: 6,
              },
              yaxis: {
                labels: {
                  formatter: val => val?.toLocaleString(),
                },
              },
              annotations: {
                xaxis: ChartHelpers.getXAxisRangeAnnotation(
                  t,
                  startDate,
                  endDate,
                  dataGranularity,
                ),
              },
            }}
          />
        </Grid>
      </Grid>
      <DaiDivider />
      <Grid size={{ xs: 12 }}>
        <StoresProductTable
          filters={{
            endDate: endDate || '',
            startDate: startDate || '',
            upcs: id ? [id] : [],
            storeIds: storeIds || undefined,
          }}
        />
      </Grid>
    </>
  )
}
